import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Info, 
  Clock, 
  Loader2,
  AlertTriangle 
} from 'lucide-react'
import React from 'react'
import { cn } from '@/lib/utils'

export interface StatusIndicatorProps {
  status: 'success' | 'error' | 'warning' | 'info' | 'pending' | 'loading' | 'idle'
  message?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'subtle' | 'outline'
  className?: string
  showIcon?: boolean
  'data-testid'?: string
}

/**
 * StatusIndicator - A reusable status display component
 * 
 * Features:
 * - Multiple status types with appropriate icons and colors
 * - Optional status message
 * - Different sizes and variants
 * - Accessible with proper aria labels
 * - Consistent with design system
 */
export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  message,
  size = 'md',
  variant = 'default',
  className,
  showIcon = true,
  'data-testid': testId
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: CheckCircle,
          colorClasses: {
            default: 'text-green-700 bg-green-50 border-green-200',
            subtle: 'text-green-600 bg-green-50/50',
            outline: 'text-green-700 border-green-300 bg-transparent'
          },
          iconColorClass: 'text-green-600'
        }
      case 'error':
        return {
          icon: XCircle,
          colorClasses: {
            default: 'text-red-700 bg-red-50 border-red-200',
            subtle: 'text-red-600 bg-red-50/50',
            outline: 'text-red-700 border-red-300 bg-transparent'
          },
          iconColorClass: 'text-red-600'
        }
      case 'warning':
        return {
          icon: AlertTriangle,
          colorClasses: {
            default: 'text-amber-700 bg-amber-50 border-amber-200',
            subtle: 'text-amber-600 bg-amber-50/50',
            outline: 'text-amber-700 border-amber-300 bg-transparent'
          },
          iconColorClass: 'text-amber-600'
        }
      case 'info':
        return {
          icon: Info,
          colorClasses: {
            default: 'text-blue-700 bg-blue-50 border-blue-200',
            subtle: 'text-blue-600 bg-blue-50/50',
            outline: 'text-blue-700 border-blue-300 bg-transparent'
          },
          iconColorClass: 'text-blue-600'
        }
      case 'pending':
        return {
          icon: Clock,
          colorClasses: {
            default: 'text-gray-700 bg-gray-50 border-gray-200',
            subtle: 'text-gray-600 bg-gray-50/50',
            outline: 'text-gray-700 border-gray-300 bg-transparent'
          },
          iconColorClass: 'text-gray-600'
        }
      case 'loading':
        return {
          icon: Loader2,
          colorClasses: {
            default: 'text-blue-700 bg-blue-50 border-blue-200',
            subtle: 'text-blue-600 bg-blue-50/50',
            outline: 'text-blue-700 border-blue-300 bg-transparent'
          },
          iconColorClass: 'text-blue-600 animate-spin'
        }
      default: // idle
        return {
          icon: AlertCircle,
          colorClasses: {
            default: 'text-gray-500 bg-gray-50 border-gray-200',
            subtle: 'text-gray-500 bg-gray-50/50',
            outline: 'text-gray-500 border-gray-300 bg-transparent'
          },
          iconColorClass: 'text-gray-500'
        }
    }
  }

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'h-3 w-3',
          gap: 'gap-1'
        }
      case 'lg':
        return {
          container: 'px-4 py-3 text-base',
          icon: 'h-5 w-5',
          gap: 'gap-3'
        }
      default: // md
        return {
          container: 'px-3 py-2 text-sm',
          icon: 'h-4 w-4',
          gap: 'gap-2'
        }
    }
  }

  const statusConfig = getStatusConfig()
  const sizeClasses = getSizeClasses()
  const Icon = statusConfig.icon

  const getAriaLabel = () => {
    if (message) return `${status}: ${message}`
    return status
  }

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-md border font-medium',
        sizeClasses.container,
        sizeClasses.gap,
        statusConfig.colorClasses[variant],
        className
      )}
      role="status"
      aria-label={getAriaLabel()}
      data-testid={testId}
    >
      {showIcon && (
        <Icon 
          className={cn(
            sizeClasses.icon,
            statusConfig.iconColorClass
          )} 
        />
      )}
      {message && (
        <span className="font-medium">
          {message}
        </span>
      )}
    </div>
  )
}

export default StatusIndicator
